import React, { useState } from "react";
import { Control, Controller } from "react-hook-form";
import { FormSection } from "./FormSection";
import { Button, ButtonGroup, Checkbox, Combobox, Field, Input, Tooltip, Switch, Label } from "@/components/ui";
import { cn } from "@/lib/utils";
import { LockIcon, UnlockIcon, HelpCircle } from "lucide-react";
import { FormSchemaType } from "../FormEditView";
import { FormPersonalizationHelpDialog } from "../FormPersonalizationHelpDialog";
import { usePageThemes } from "@/utils";

interface SettingsSectionProps {
  control: Control<FormSchemaType>;
  initialThemeId?: number;
  isSubmitting: boolean;
}

export function SettingsSection({ control, initialThemeId, isSubmitting }: SettingsSectionProps) {
  const { themes, isLoading } = usePageThemes({ page: 1, perPage: 50 }, initialThemeId);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  return (
    <FormSection id="section-settings" title="Form Settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <Field label="Form Status" name="active">
                <ButtonGroup>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={cn(
                      field.value === false
                        ? "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
                        : ""
                    )}
                    disabled={isSubmitting}
                    onClick={() => field.onChange(false)}
                    aria-pressed={field.value === false}
                  >
                    Draft
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={cn(
                      field.value === true
                        ? "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
                        : ""
                    )}
                    disabled={isSubmitting}
                    onClick={() => field.onChange(true)}
                    aria-pressed={field.value === true}
                  >
                    Published
                  </Button>
                </ButtonGroup>
              </Field>
            )}
          />

          <Controller
            name="displayName"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label>Form Name</Field.Label>
                <Input {...field} placeholder="e.g. Client Feedback Form" />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />

          <Controller
            name="contentPageThemeId"
            control={control}
            render={({ field }) => (
              <Field
                name="contentPageThemeId"
                label={
                  <div className="flex items-center gap-2">
                    <div>Theme</div>
                    <Controller
                      name="allowThemeChange"
                      control={control}
                      render={({ field: lockField }) => (
                        <Tooltip>
                          <Tooltip.Trigger
                            render={
                              <Button
                                type="button"
                                size="icon-sm"
                                className="size-7"
                                variant={lockField.value ? "outline" : "default"}
                                onClick={() => lockField.onChange(!lockField.value)}
                                aria-label={lockField.value ? "Lock Theme" : "Unlock Theme"}
                              >
                                {lockField.value ? (
                                  <UnlockIcon className="size-3.5" />
                                ) : (
                                  <LockIcon className="size-3.5" />
                                )}
                              </Button>
                            }
                          />
                          <Tooltip.Content>
                            {lockField.value ? "Lock Theme to prevent changes" : "Unlock to allow Theme changes"}
                          </Tooltip.Content>
                        </Tooltip>
                      )}
                    />
                  </div>
                }
              >
                <Combobox
                  items={themes}
                  value={themes.find(t => t.id === field.value) || null}
                  onValueChange={val => {
                    field.onChange(val ? val.id : null);
                  }}
                  itemToStringLabel={item => item?.name || ""}
                  isItemEqualToValue={(a, b) => a?.id === b?.id}
                  placeholder="Use Organization Defaults"
                  showClear
                  searchable
                  isLoadingMore={isLoading}
                  disabled={isSubmitting}
                  className="w-full"
                />
              </Field>
            )}
          />
        </div>

        <div className="space-y-4">
          <Field
            label={
              <div className="flex items-center justify-between w-full">
                <span>Personalization & Languages</span>
                <button
                  type="button"
                  onClick={() => setHelpDialogOpen(true)}
                  className="text-sm text-primary hover:underline font-medium focus:outline-none flex items-center gap-1.5 normal-case font-normal"
                >
                  <span>Learn More</span>
                  <HelpCircle className="size-4 shrink-0" />
                </button>
              </div>
            }
            name="settings"
          >
            <div className="flex flex-col gap-5 pt-1.5">
              {/* Supported Languages Section */}
              <div className="flex flex-col gap-1.5">
                <Label className="font-semibold">Supported Languages</Label>
                <div className="flex items-center gap-6 ml-0 mt-1">
                  <Controller
                    name="supportedLanguages"
                    control={control}
                    render={({ field }) => {
                      const value = field.value || ["en"];
                      const isEnglishChecked = value.includes("en");
                      const isSpanishChecked = value.includes("es");

                      const handleCheckboxChange = (lang: "en" | "es", checked: boolean) => {
                        let newValue = [...value];
                        if (checked) {
                          if (!newValue.includes(lang)) {
                            newValue.push(lang);
                          }
                        } else {
                          // Prevent unchecking all languages
                          if (newValue.length > 1) {
                            newValue = newValue.filter((item: string) => item !== lang);
                          }
                        }
                        field.onChange(newValue);
                      };

                      return (
                        <div className="flex items-center gap-6">
                          <Checkbox
                            id="lang-en"
                            label="English"
                            checked={isEnglishChecked}
                            onCheckedChange={checked => handleCheckboxChange("en", !!checked)}
                          />
                          <Checkbox
                            id="lang-es"
                            label="Spanish"
                            checked={isSpanishChecked}
                            onCheckedChange={checked => handleCheckboxChange("es", !!checked)}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground leading-normal mt-1">
                  Select languages for this form. If more than one language is selected, automatic translation will be
                  enabled.
                </p>
              </div>

              {/* Personalization Section */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <Controller
                    name="allowAiPersonalization"
                    control={control}
                    render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />}
                  />
                  <Label className="font-semibold">AI Personalization</Label>
                </div>
                <p className="text-xs text-muted-foreground leading-normal ml-11">
                  Enabling this will personalize each form to the Player and Fan using AI.
                </p>
              </div>
            </div>
          </Field>
        </div>
      </div>

      {/* Help Dialog */}
      <FormPersonalizationHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
    </FormSection>
  );
}
